import {
  Box,
  Button,
  Form,
  Card,
  Icon,
  Text,
  Heading,
  Flex,
  Input,
  FormControl,
  FormErrorMessage,
  FormLabel
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import * as Sentry from '@sentry/browser'
import { useIntl, defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { RefiDownSvg } from './RefiDownSvg'
import { useTrackingContext } from '../../../../providers/tracking'
import { flashNotification } from '../../../../actions/notification'
import { sendRefiRequestForClient } from '../../../../actions/refinance'
import { selectSponsorshipFirstName } from '../../../../store/selectors/sponsorship'
import { selectCustomerFirstName } from '../../../../store/selectors/customerProfile'

export const MSG = defineMessages({
  head: {
    id: 'RefiModule.negativeRefiInfo.head',
    defaultMessage: "Refi rates may be higher than your home's rate."
  },
  body: {
    id: 'RefiModule.negativeRefiInfo.body',
    defaultMessage:
      'Ask {name} for more info: they might be able to offer different rates, loan options, and answer questions about your specific scenario.'
  },
  label: {
    id: 'RefiModule.negativeRefiInfo.input.label',
    defaultMessage: 'Message {name}'
  },
  defaultVal: {
    id: 'RefiModule.negativeRefiInfo.input.default',
    defaultMessage: 'What are my refi options?'
  },
  btn: {
    id: 'RefiModule.negativeRefiInfo.send',
    defaultMessage: 'Send'
  },
  required: {
    id: 'RefiModule.negativeRefiInfo.input.required',
    defaultMessage: 'Must provide message to send'
  },
  success: {
    id: 'RefiModule.negativeRefiInfo.success',
    defaultMessage: 'Sent!'
  },
  error: {
    id: 'RefiModule.negativeRefiInfo.error',
    defaultMessage: 'Error sending message. Please try again!'
  }
})

export const NegativeRefiInfo = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const { trackingClient } = useTrackingContext()
  const customerFirstName = useSelector(selectCustomerFirstName)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)
  const firstName = sponsorFirstName ? sponsorFirstName : customerFirstName

  const handleSubmit = async val => {
    setLoading(true)

    try {
      await dispatch(sendRefiRequestForClient(val.message, trackingClient))
      await dispatch(flashNotification({ message: intl.formatMessage(MSG.success), messageType: 'success' }))
    } catch (e) {
      Sentry.captureException(e)
      await dispatch(flashNotification({ message: intl.formatMessage(MSG.error), messageType: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box px={2}>
      <Card mb={2} w={12} h={12} display='flex' borderRadius='50%' alignItems='center' justifyContent='center'>
        <RefiDownSvg />
      </Card>
      <Heading as='h4' mb={2} size='sm'>
        {intl.formatMessage(MSG.head)}
      </Heading>
      <Text size='xs'>{intl.formatMessage(MSG.body, { name: firstName })}</Text>
      <Form defaultValues={{ message: intl.formatMessage(MSG.defaultVal) }} onSubmit={val => handleSubmit(val)}>
        {({ formState: { errors } }) => (
          <Flex direction='row' gap='2'>
            <FormControl isInvalid={!!errors?.message}>
              <FormLabel>{intl.formatMessage(MSG.label, { name: firstName })}</FormLabel>
              <Input
                size='sm'
                name='message'
                isDisabled={loading}
                rules={{
                  validate: {
                    required: (v: string) => v?.length > 0 || intl.formatMessage(MSG.required)
                  }
                }}
              />
              <FormErrorMessage>{errors?.message?.message?.toString()}</FormErrorMessage>
            </FormControl>
            <Button
              mt={8}
              size='sm'
              type='submit'
              flex='1 0 auto'
              isLoading={loading}
              isDisabled={loading}
              leftIcon={<Icon name='message' />}
            >
              {intl.formatMessage(MSG.btn)}
            </Button>
          </Flex>
        )}
      </Form>
    </Box>
  )
}
